import { PaymentResult, AtonePaymentMethod } from '../../iap.types';
import { useEffect, useState } from 'preact/hooks';
import {
  AtoneServiceType,
  ProductId,
  webIAPPrices,
} from 'src/replicant/iap/iap.ruleset';
import {
  atoneInitPayment,
  AtonePaymentError,
  connectAtoneSDK,
} from '../../providers/atone';
import { PayButton } from '../PayButton';
import { getProductName } from '../Config';

/**
 * Handle atone payments. Show required fields and forms according to the payment method.
 * @param
 * @returns
 */
export function AtoneHandler({
  productId,
  paymentMethod,
  close,
  onPurchaseStart,
}: {
  productId: ProductId;
  paymentMethod: AtonePaymentMethod;
  close: (result: PaymentResult) => void;
  onPurchaseStart: () => void;
}) {
  // Initialized.
  const [initialized, setInitialized] = useState(false);

  // Whether the form is submitted.
  const [submitted, setSubmitted] = useState(false);

  // On mount.
  useEffect(() => {
    // Load atone sdk.
    connectAtoneSDK().then((_) => {
      setInitialized(true);
    });
  }, []);

  // Handle the payment.
  const handleAtonePayment = async (e: Event) => {
    // Prevent multiple submissions.
    if (submitted) return;
    setSubmitted(true);

    e.preventDefault();

    onPurchaseStart();

    try {
      const type =
        paymentMethod === 'atone_bnpl'
          ? AtoneServiceType.ATONE
          : AtoneServiceType.ATONE_LITE;

      const { transaction_id, shop_transaction_no } = await atoneInitPayment({
        type,
        productId: productId,
        price: webIAPPrices[productId],
        amount: 1,
        description: getProductName(productId),
      });

      close({
        success: true,
        provider: 'atone',
        purchaseID: transaction_id,
        internalTransactionId: shop_transaction_no,
        transactionType: type,
      });
    } catch (e: any) {
      // Handle atone errors.
      if (e instanceof AtonePaymentError) {
        close({
          success: false,
          provider: 'atone',
          code: e.code,
          error: e.message,
          purchaseID: 'unknown',
          internalTransactionId: e.payment.shop_transaction_no,
        });
        return;
      }

      // Handle other errors.
      close({
        success: false,
        provider: 'atone',
        code: 'CLIENT_ERROR',
        error: e.message ?? e ?? 'Unknown error',
        purchaseID: 'unknown',
        internalTransactionId: 'unknown',
      });
    } finally {
      setSubmitted(false);
    }
  };

  return (
    <>
      <PayButton
        disabled={!initialized || submitted}
        loading={!initialized || submitted}
        onClick={handleAtonePayment}
      />
    </>
  );
}
